const LAZY_CHUNK_SCRIPT_ATTRIBUTE = "data-webpack";
const RELOADED_FROM_BUILD_STORAGE_KEY = "staleBuildReload.reloadedFromBuildId";

let installed = false;
let checking = false;

function readReloadedFromBuildId(): string | null {
  try {
    return sessionStorage.getItem(RELOADED_FROM_BUILD_STORAGE_KEY);
  } catch {
    return null;
  }
}

// 기록하지 못하면 새로 고친 뒤 같은 빌드가 다시 와도 막을 수 없으므로, 새로 고치지 않는다
function writeReloadedFromBuildId(buildId: string): boolean {
  try {
    sessionStorage.setItem(RELOADED_FROM_BUILD_STORAGE_KEY, buildId);
    return true;
  } catch {
    return false;
  }
}

async function isBuildRemoved(buildId: string): Promise<boolean> {
  try {
    const response = await fetch(`/_next/static/${buildId}/_buildManifest.js`, {
      method: "HEAD",
      cache: "no-store",
    });
    return response.status === 404;
  } catch {
    return false;
  }
}

function isLazyChunkScript(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLScriptElement &&
    target.hasAttribute(LAZY_CHUNK_SCRIPT_ATTRIBUTE)
  );
}

async function reloadIfBuildRemoved(): Promise<void> {
  const buildId = window.__NEXT_DATA__?.buildId;
  if (!buildId || checking) return;
  const alreadyReloadedFromThisBuild = readReloadedFromBuildId() === buildId;
  if (alreadyReloadedFromThisBuild) return;

  checking = true;
  const removed = await isBuildRemoved(buildId);
  checking = false;

  if (removed && writeReloadedFromBuildId(buildId)) window.location.reload();
}

/**
 * 배포가 실행 중인 빌드를 갈아 끼운 뒤 그 빌드의 지연 청크를 받지 못하면 페이지를 새로 고친다.
 *
 * 배포는 이전 빌드의 `/_next/static` 을 지운다. 배포 전에 연 탭이 `next/dynamic`·`import()` 로 청크를 처음 부르면 404 가
 * 나는데, `next/dynamic` 은 production 에서 이 실패를 던지지 않고 아무것도 그리지 않아 화면이 비고 `_error` 의 새로고침도
 * 타지 않는다. 페이지 청크 실패는 Next 라우터가 스스로 새로 고치므로 webpack 지연 청크(`data-webpack` 스크립트)만 본다.
 *
 * 실행 중인 빌드의 매니페스트가 404 일 때만 새로 고친다 — 일시적 네트워크 실패나 현재 빌드에 없는 청크는 새로 고쳐도
 * 낫지 않는다. 한 빌드에서는 한 번만 새로 고친다(새로 고친 뒤에도 같은 빌드가 오면 반복하지 않는다).
 *
 * `_app` 모듈 스코프(브라우저)에서 부른다. 다시 불러도 무동작이다.
 */
export function installStaleBuildReload(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener(
    "error",
    (event) => {
      if (isLazyChunkScript(event.target)) void reloadIfBuildRemoved();
    },
    true
  );
}
